import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router';
import { ApiError, endpoints } from '../api';
import { useSession } from '../session';
import { Button, ErrorNote, FullPageMessage } from '../ui';
import { PLATFORM_NAME } from '@bre-ticketing/contracts';

/**
 * Pair once with the code from Admin → Devices, then the staff PIN each
 * shift (docs/auth.md §3.1–3.2). The PIN pad is the same as the window's,
 * sized for a phone held in one hand.
 */
export default function LoginPage() {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'pin' | 'pair'>('pin');
  const [pin, setPin] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: (value: string) => endpoints.pinLogin(value),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries();
      await navigate('/', { replace: true });
    },
    onError: (cause: ApiError) => {
      setPin('');
      if (cause.code === 'DEVICE_TOKEN_REQUIRED' || cause.code === 'DEVICE_NOT_RECOGNIZED') {
        setMode('pair');
        setError('This device is not paired yet. Enter its pairing code from Admin → Devices.');
      } else setError(cause.message);
    },
  });
  const pair = useMutation({
    mutationFn: (value: string) => endpoints.pair(value),
    onSuccess: () => {
      setError(null);
      setMode('pin');
      setCode('');
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (session.isPending) return <FullPageMessage title="Checking your session…" />;
  if (session.data) return <Navigate to="/" replace />;

  const press = (digit: string) => {
    if (mode !== 'pin' || pin.length >= 6) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === 6) login.mutate(next);
  };

  return (
    <div className="safe-top safe-bottom grid min-h-full place-items-center p-6">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-semibold">
          <span className="font-display font-bold tracking-tight">{PLATFORM_NAME}</span> <span className="text-gold">Scanner</span>
        </h1>
        <p className="mt-1 text-sm text-white/60">{mode === 'pin' ? 'Enter your PIN' : 'Pair this device'}</p>
        {error ? (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        ) : null}
        {mode === 'pin' ? (
          <>
            <div className="mt-8 flex justify-center gap-3" aria-label="PIN entry">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <span key={i} className={`h-4 w-4 rounded-full ${i < pin.length ? 'bg-gold' : 'bg-shell-700'}`} />
              ))}
            </div>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key, i) => (
                <button key={i} type="button" disabled={key === '' || login.isPending} onClick={() => (key === '⌫' ? setPin(pin.slice(0, -1)) : press(key))} className={`h-20 rounded-2xl text-3xl font-semibold ${key === '' ? 'invisible' : 'bg-shell-700 active:bg-shell-500'}`}>
                  {key}
                </button>
              ))}
            </div>
            <Button variant="ghost" className="mt-6 w-full text-sm" onClick={() => setMode('pair')}>
              Pair this device
            </Button>
          </>
        ) : (
          <form
            className="mt-6 space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              pair.mutate(code.trim().toUpperCase());
            }}
          >
            <input value={code} onChange={(e) => setCode(e.target.value)} autoFocus placeholder="Pairing code" maxLength={8} className="touch w-full rounded-xl border border-shell-500 bg-shell-800 px-4 text-center text-2xl tracking-[0.3em] uppercase" aria-label="Pairing code" />
            <Button variant="primary" type="submit" disabled={code.trim().length < 8 || pair.isPending} className="w-full">
              Pair
            </Button>
            <Button variant="ghost" className="w-full text-sm" onClick={() => setMode('pin')}>
              Back to PIN
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
